import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SEO } from '../components/SEO';
import { collectiveReadings } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CollectiveMarch2026Page = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  // Get previous readings for archive (exclude current)
  const previousReadings = collectiveReadings.filter(r => r.slug !== 'march-2026');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email,
        source: 'collective-march-2026'
      });
      setSubscribed(true);
      setEmail('');
      toast.success('You\'re subscribed', {
        description: 'You\'ll receive each month\'s reflection in your inbox.'
      });
    } catch (error) {
      toast.error('Something went wrong', {
        description: 'Please try again.'
      });
    }
    setIsSubmitting(false);
  };

  return (
    <div className="bg-[var(--color-charcoal)]">
      <SEO 
        title="March 2026 Collective Reflection"
        description="March 2026 reflection exploring personal authority, grounded action, and embodied resilience."
        path="/collective/march-2026"
      />

      {/* Full-Width Hero */}
      <section className="relative min-h-[70vh] flex items-end">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1757265500084-e3a97ddf007e?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-charcoal)] via-[var(--color-charcoal)]/70 to-transparent" />
        </div>
        
        <div className="relative w-full pb-16 pt-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            >
              <Link 
                to="/collective" 
                className="inline-flex items-center gap-2 text-[var(--color-gold)] text-sm tracking-[0.2em] uppercase mb-8 hover:opacity-80 transition-opacity"
              >
                <ArrowLeft className="w-4 h-4" />
                The Collective
              </Link>
              
              <p className="text-[var(--color-gold)] text-lg tracking-[0.2em] uppercase mb-4">
                March 2026
              </p>
              
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl max-w-4xl leading-[1.05]">
                Stepping Into Personal Authority
              </h1>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Before You Read - Accent Block */}
      <section className="py-16 bg-[var(--color-deep-red)]/15 border-y border-[var(--color-deep-red)]/20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-[var(--color-cream)]/80 leading-[2] text-lg italic">
              Pause. Take one slow breath. Let your body settle.<br />
              This reading is not a prediction. It is a mirror.<br />
              Notice what resonates. Leave what does not.<br />
              <span className="text-[var(--color-gold)]">Ask yourself quietly: Where am I ready to take responsibility for my next step?</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <article className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          {/* The Energy of March - Two Column */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-gold)] sticky top-32">
                The Energy of March
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>March carries a shift.</p>
              <p>Where recent months may have asked for reflection, this one asks for movement — not hurried, not reactive, but grounded and deliberate.</p>
              <p>At the centre of this month is the archetype of personal authority. Leadership not over others, but over the self. The willingness to move from contemplation into manifestation. To stop circling and begin structuring.</p>
              <p className="text-[var(--color-cream)]">Authority here does not mean control. It means responsibility. It means recognising where you have agency — and choosing to use it.</p>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-24">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)]/30 to-transparent" />
          </div>

          {/* What Is Completing - Two Column */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-bottle-green-light)] sticky top-32">
                What Is Completing
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>Several patterns may be ready to fall away.</p>
              <p>Overthinking. Rumination. Replaying old wounds. Projecting imagined futures.</p>
              <p>Subtle forms of escape — distraction, avoidance, numbing, romanticising what keeps you small.</p>
              <p>The slow, protective energy of winter may have served you. But hibernation cannot become identity.</p>
              <p>This month invites conscious closure. To acknowledge what has run its course. To release patterns that once protected you but now limit you.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">Letting go is not loss. It is preparation.</p>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-24">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)]/30 to-transparent" />
          </div>

          {/* The Invitation - Two Column */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-gold)] sticky top-32">
                The Invitation
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>Examine your resistance honestly.</p>
              <p className="text-[var(--color-cream)]">Where are you hesitating? What are you afraid will shift if you act?</p>
              <p>Transformation this month is not dramatic. It is mature. It asks for one clear decision. One structured step.</p>
              <p>Spiritual insight without grounded action becomes avoidance. Vision without participation becomes fantasy.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">Feel deeply. Then move deliberately. Trust the unfolding — and participate in it.</p>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-24">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)]/30 to-transparent" />
          </div>

          {/* Self-Care & Embodiment - Two Column */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-deep-red-light)] sticky top-32">
                Self-Care & Embodiment
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>This month also speaks of life force.</p>
              <p className="text-[var(--color-cream)]">Passion. Sexuality. Creative energy.</p>
              <p>Not as performance. Not as indulgence. But as vitality.</p>
              <p>Where have you disconnected from your body's wisdom? Where has shame or silence muted your desires?</p>
              <p>Channel that energy consciously — into creativity, movement, intention.</p>
              <p>If deeper wounds surface, seek support where needed. Strength includes discernment.</p>
              <p className="text-[var(--color-cream)]">Authority without vitality becomes rigid. Vitality without structure becomes scattered.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">March asks for integration.</p>
            </div>
          </motion.section>

        </div>
      </article>

      {/* Inquiry - Full Width Accent */}
      <section className="py-20 lg:py-28 bg-[var(--color-dark-purple)]/20 border-y border-[var(--color-dark-purple)]/30">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-serif text-2xl text-[var(--color-gold)] mb-8">Inquiry</h2>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-[var(--color-cream)] leading-[1.4] mb-6">
              Where am I ready to stop postponing and start participating in my own becoming?
            </p>
            <p className="text-[var(--color-cream)]/60 text-lg">
              Choose one concrete action. Anchor it.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <h2 className="font-serif text-2xl text-[var(--color-gold)] mb-8">Closing</h2>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              March does not demand perfection.
            </p>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              It asks for steadiness. For ownership. For movement that aligns with who you are becoming.
            </p>
            <p className="font-serif text-2xl md:text-3xl text-[var(--color-cream)] mt-8">
              Move when ready. But do move.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Continue the Work */}
      <section className="py-20 bg-[var(--color-charcoal-light)]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Tarot Session */}
              <div>
                <h3 className="font-serif text-2xl text-[var(--color-cream)] mb-4">Continue the Work</h3>
                <p className="text-[var(--color-cream)]/70 leading-[1.8] mb-6">
                  If this month's reflection stirred something you would like to explore more deeply, you are welcome to book a reflective tarot session.
                </p>
                <p className="text-[var(--color-cream)]/60 leading-[1.8] mb-6 text-sm">
                  Tarot here is not predictive. It is a grounded conversation that supports clarity, emotional resilience, and conscious choice.
                </p>
                <Link
                  to="/tarot"
                  className="inline-flex items-center gap-2 text-[var(--color-gold)] hover:text-[var(--color-gold-light)] transition-colors group"
                >
                  Book a Tarot Session
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              {/* Newsletter */}
              <div>
                <h3 className="font-serif text-2xl text-[var(--color-cream)] mb-4">Monthly Reflections</h3>
                <p className="text-[var(--color-cream)]/70 leading-[1.8] mb-6">
                  Receive each month's Collective Reflection directly in your inbox.
                </p>
                
                {subscribed ? (
                  <p className="text-[var(--color-gold)]">Thank you. You'll receive the next reflection in your inbox.</p>
                ) : (
                  <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email address"
                      required
                      className="w-full px-5 py-3 bg-[var(--color-charcoal)] border border-[var(--color-dark-purple)]/30 rounded-lg text-[var(--color-cream)] placeholder:text-[var(--color-cream)]/40 focus:outline-none focus:border-[var(--color-gold)]/50 transition-colors"
                      data-testid="collective-march-email"
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-6 py-3 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-medium rounded-lg hover:bg-[var(--color-gold-light)] transition-all duration-300 disabled:opacity-50"
                      data-testid="collective-march-submit"
                    >
                      {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                    </button>
                    <p className="text-xs text-[var(--color-cream)]/40 text-center">
                      One reflection per month. No noise.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Previous Reflections */}
      <section className="py-12 border-t border-[var(--color-dark-purple)]/20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <details className="group">
            <summary className="flex items-center justify-between cursor-pointer py-4 text-[var(--color-cream)]/60 hover:text-[var(--color-cream)] transition-colors">
              <span className="font-serif text-lg">Previous Collective Reflections</span>
              <ChevronDown className="w-5 h-5 group-open:rotate-180 transition-transform" />
            </summary>
            <div className="pt-4 pb-8 space-y-4">
              {previousReadings.length > 0 ? (
                previousReadings.map((reading) => (
                  <Link
                    key={reading.id}
                    to={`/collective/${reading.slug}`}
                    className="block p-4 bg-[var(--color-charcoal-light)] rounded-lg hover:bg-[var(--color-dark-purple)]/20 transition-colors"
                  >
                    <p className="text-[var(--color-gold)] text-sm mb-1">{reading.month}</p>
                    <p className="text-[var(--color-cream)]">{reading.title}</p>
                  </Link>
                ))
              ) : (
                <p className="text-[var(--color-cream)]/50 italic">March 2026 is the first Collective Reflection.</p>
              )}
            </div>
          </details>
        </div>
      </section>
    </div>
  );
};
